import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { TATStatusLegend } from "@/components/TATStatusLegend";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, Clock, AlertCircle, CheckCircle2, Calendar, ListTodo, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTasks } from "@/hooks/useTasks";
import { useAllUsers } from "@/hooks/useAllUsers";
import { useTotalElapsedTime } from "@/hooks/useTotalElapsedTime";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatTATCountdown, getTATColorClass, getElapsedTimeColorClass, getTATBadgeVariant } from "@/lib/tatHelpers";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useActivityUpdate } from "@/hooks/useActivityUpdate";
import { useAuth } from "@/hooks/useAuth";

const formatMinutesToTime = (minutes?: number): string => {
  if (!minutes) return "00:00:00";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.floor((minutes % 1) * 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Component to display patient's total elapsed time from MD completion
const PatientTotalElapsedTime = ({ patientId }: { patientId?: string }) => {
  const { data: mdCompletionTime } = useMDCompletionTime(patientId);
  const elapsedTime = useTotalElapsedTime(mdCompletionTime);
  
  return (
    <div className="text-sm font-medium font-mono text-foreground">
      {elapsedTime}
    </div>
  );
};

const TimeSinceAssigned = ({ assignedAt }: { assignedAt?: string }) => {
  const [timeElapsed, setTimeElapsed] = useState("00:00:00");

  useEffect(() => {
    if (!assignedAt) {
      setTimeElapsed("00:00:00");
      return;
    }

    const calculateTime = () => {
      try {
        const assigned = new Date(assignedAt);
        const now = new Date();
        const diffMs = now.getTime() - assigned.getTime();
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } catch {
        return "00:00:00";
      }
    };

    setTimeElapsed(calculateTime());

    const interval = setInterval(() => {
      setTimeElapsed(calculateTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [assignedAt]);

  return (
    <div className="text-sm font-medium font-mono text-muted-foreground">
      {timeElapsed}
    </div>
  );
};

const TimeTakenToStart = ({ assignedAt, startedAt }: { assignedAt?: string; startedAt?: string }) => {
  if (!assignedAt || !startedAt) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }
  
  const assigned = new Date(assignedAt);
  const started = new Date(startedAt);
  const diffMs = started.getTime() - assigned.getTime();
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  return (
    <div className="text-sm font-medium font-mono text-muted-foreground">
      {`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
    </div>
  );
};

const validMyTaskTabs = ["upcoming", "todo", "in-progress", "completed"] as const;
type MyTaskTab = typeof validMyTaskTabs[number];

const MyTasks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUser, setSelectedUser] = useState<string>("all");
  
  const tabParam = searchParams.get("tab");
  const activeTab: MyTaskTab = tabParam && (validMyTaskTabs as readonly string[]).includes(tabParam) 
    ? (tabParam as MyTaskTab) 
    : "in-progress";

  const setActiveTab = (tab: MyTaskTab) => {
    setSearchParams({ tab }, { replace: true });
  };
  const [assigneesByTask, setAssigneesByTask] = useState<Record<string, string | undefined>>({});
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { updateActivity, isUpdating } = useActivityUpdate();
  const { userProfile } = useAuth();

  // Fetch real tasks from database (elapsed time calculated in IST)
  const { data: allTasks = [], isLoading, refetch } = useTasks(selectedUser === "all" ? undefined : selectedUser);

  // Fetch all users from profiles table for the dropdown
  const { data: allUsers = [] } = useAllUsers();

  // Calculate task counts for all statuses (exclude skipped tasks)
  const taskCounts = useMemo(() => {
    const isUpcomingTask = (task: (typeof allTasks)[number]) =>
      task.status === 'pending' && !task.completionTime && !task.completedBy;

    let upcoming = 0;
    let todo = 0;
    let inProgress = 0;
    let completed = 0;
    
    allTasks.forEach(task => {
      // Skip 'skipped' tasks - they should not be counted
      if (task.status === 'skipped') return;
      
      if (isUpcomingTask(task)) {
        upcoming++;
      } else if (task.status === 'in-progress' && task.assignedAt && !task.startedAt) {
        todo++;
      } else if (task.status === 'in-progress' && task.startedAt) {
        inProgress++;
      } else if (task.status === 'completed') {
        completed++;
      }
    });
    
    return { upcoming, todo, inProgress, completed };
  }, [allTasks]);

  // Filter tasks based on active tab (exclude skipped tasks from all tabs)
  const filteredTasks = useMemo(() => {
    // First filter out skipped tasks - they should never appear
    let filtered = allTasks.filter(task => task.status !== 'skipped');

    if (activeTab === "upcoming") {
      // Tasks that are pending and not yet completed
      filtered = filtered.filter(
        task => task.status === 'pending' && !task.completionTime && !task.completedBy
      );
    } else if (activeTab === "todo") {
      // Tasks that are in-progress but not yet started by user
      filtered = filtered.filter(task => 
        task.status === 'in-progress' && 
        task.assignedAt !== undefined &&
        !task.startedAt
      );
    } else if (activeTab === "in-progress") {
      // Tasks that user has actually started
      filtered = filtered.filter(task => 
        task.status === 'in-progress' && 
        !!task.startedAt
      );
      
      // Sort in-progress tasks by TAT urgency, then elapsed time
      return filtered.sort((a, b) => {
        const tatPriority: Record<string, number> = {
          'exceeded': 4,
          'red': 3,
          'yellow': 2,
          'green': 1,
        };
        
        const aPriority = a.tatStatus ? tatPriority[a.tatStatus] || 0 : 0;
        const bPriority = b.tatStatus ? tatPriority[b.tatStatus] || 0 : 0;
        
        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }
        
        return b.elapsedMinutes - a.elapsedMinutes;
      });
    } else if (activeTab === "completed") {
      filtered = filtered.filter(task => task.status === "completed");
    }

    return filtered.sort((a, b) => b.elapsedMinutes - a.elapsedMinutes);
  }, [allTasks, activeTab]);

  // Setup realtime subscription for tasks
  useEffect(() => {
    const channel = supabase
      .channel('tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activities'
        },
        () => {
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patients'
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const handleStartTask = async (taskId: string, patientId: string) => {
    const result = await updateActivity({
      activityId: taskId,
      patientId: patientId,
      updates: {
        status: 'in-progress',
        started_at: new Date().toISOString(),
      }
    });
    
    if (result.success) {
      refetch();
    }
  };

  const handleCompleteTask = async (taskId: string, patientId: string, assigneeId?: string) => {
    const updates: Record<string, any> = {
      status: 'completed',
      completion_time: new Date().toISOString(),
      // Mark completed in both activity and profile (completed_by links to profile for display & team stats)
      completed_by: userProfile?.id ?? undefined,
    };
    if (assigneeId) {
      updates.assigned_to = assigneeId;
    }
    const result = await updateActivity({
      activityId: taskId,
      patientId: patientId,
      updates,
    });
    if (result.success) {
      refetch();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Tab Menu */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Upcoming Tab */}
              <button
                onClick={() => setActiveTab("upcoming")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors relative min-h-[48px] min-w-[48px]",
                  activeTab === "upcoming" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Calendar className="w-4 h-4" />
                {(!isMobile || activeTab === "upcoming") && (
                  <>
                    <span>Upcoming</span>
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded-full",
                      activeTab === "upcoming" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {taskCounts.upcoming}
                    </span>
                  </>
                )}
                {activeTab === "upcoming" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>

              {/* Todo Tab */}
              <button
                onClick={() => setActiveTab("todo")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors relative min-h-[48px] min-w-[48px]",
                  activeTab === "todo" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <ListTodo className="w-4 h-4" />
                {(!isMobile || activeTab === "todo") && (
                  <>
                    <span>Todo</span>
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded-full",
                      activeTab === "todo" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {taskCounts.todo}
                    </span>
                  </>
                )}
                {activeTab === "todo" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>

              {/* In-Progress Tab */}
              <button
                onClick={() => setActiveTab("in-progress")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors relative min-h-[48px] min-w-[48px]",
                  activeTab === "in-progress" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Clock className="w-4 h-4" />
                {(!isMobile || activeTab === "in-progress") && (
                  <>
                    <span>In-Progress</span>
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded-full",
                      activeTab === "in-progress" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {taskCounts.inProgress}
                    </span>
                  </>
                )}
                {activeTab === "in-progress" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
              
              {/* Completed Tab */}
              <button
                onClick={() => setActiveTab("completed")}
                className={cn(
                  "flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors relative min-h-[48px] min-w-[48px]",
                  activeTab === "completed" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <CheckCircle2 className="w-4 h-4" />
                {(!isMobile || activeTab === "completed") && (
                  <>
                    <span>Completed</span>
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded-full",
                      activeTab === "completed" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {taskCounts.completed}
                    </span>
                  </>
                )}
                {activeTab === "completed" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            </div>

            {/* User Filter on Right */}
            <div className="flex items-center gap-3">
              <div className="w-64">
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {allUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.fullName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        
        {/* TAT Status Legend - Show only on in-progress tab */}
        {activeTab === 'in-progress' && (
          <TATStatusLegend />
        )}
      </div>

      <main className="container mx-auto p-3">

        {/* Tasks Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task Name</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Ward / Bed</TableHead>
                {activeTab !== 'upcoming' && <TableHead>Assigned To</TableHead>}
                {activeTab !== 'upcoming' && <TableHead>Assigned At</TableHead>}
                <TableHead>Status</TableHead>
                {activeTab === 'todo' ? (
                  <TableHead>Actions</TableHead>
                ) : activeTab === 'completed' ? (
                  <TableHead>Completed in Time</TableHead>
                ) : activeTab === 'in-progress' ? (
                  <>
                    <TableHead>Elapsed Time (TAT)</TableHead>
                    <TableHead>Total Patient Time</TableHead>
                    {/* Actions column removed for in-progress - Complete button disabled */}
                  </>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: activeTab === 'in-progress' ? 8 : activeTab === 'todo' ? 7 : activeTab === 'completed' ? 7 : 4 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              ) : filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell 
                  colSpan={
                      activeTab === 'todo' ? 7 :
                      activeTab === 'completed' ? 7 : 
                      activeTab === 'in-progress' ? 8 :
                      4
                    }
                    className="text-center py-8 text-muted-foreground"
                  >
                    No tasks found matching the selected filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => {
                  return (
                    <TableRow 
                      key={task.id} 
                      className={cn(
                        "hover:bg-muted/50",
                        // Add left border and subtle background based on TAT status for in-progress tasks
                        "border-l-4",
                        task.status === 'in-progress' && task.tatStatus === 'green' && "border-l-success bg-success/5",
                        task.status === 'in-progress' && task.tatStatus === 'yellow' && "border-l-warning bg-warning/5",
                        task.status === 'in-progress' && task.tatStatus === 'red' && "border-l-destructive bg-destructive/5",
                        task.status === 'in-progress' && task.tatStatus === 'exceeded' && "border-l-destructive bg-destructive/10",
                        task.status !== 'in-progress' && "border-l-transparent"
                      )}
                    >
                      <TableCell>
                        <div className="font-medium text-foreground">{task.taskName}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium text-foreground">{task.patientName}</div>
                            <div className="text-xs text-muted-foreground">{task.patientId}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="text-foreground">{task.ward}</div>
                          <div className="text-xs text-muted-foreground">{task.bedRoom}</div>
                        </div>
                      </TableCell>
                      {activeTab !== 'upcoming' && (
                        <TableCell>
                          {(activeTab === 'in-progress' || activeTab === 'todo') ? (
                            // In-progress & Todo tabs: assignee dropdown per task
                            <div className="space-y-1">
                              <Select
                                value={assigneesByTask[task.id] || "unassigned"}
                                onValueChange={(value) =>
                                  setAssigneesByTask((prev) => ({
                                    ...prev,
                                    [task.id]: value === "unassigned" ? undefined : value,
                                  }))
                                }
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder={task.assignedToStaff?.fullName || "Select user"} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {allUsers.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.fullName}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            // Completed tab: show static assignee name
                            <div className="space-y-1">
                              {task.assignedToStaff ? (
                                <div className="space-y-1">
                                  <div className="font-medium text-foreground">
                                    {task.assignedToStaff.fullName}
                                  </div>
                                  <div className="flex gap-1 flex-wrap">
                                    {task.assignedToStaff.role && (
                                      <Badge variant="outline" className="text-xs">
                                        {task.assignedToStaff.role}
                                      </Badge>
                                    )}
                                    {task.assignedToStaff.department && (
                                      <Badge variant="secondary" className="text-xs">
                                        {task.assignedToStaff.department}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">Unassigned</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                      {activeTab !== 'upcoming' && (
                        <TableCell>
                          {task.assignedAt ? (
                            <div className="text-sm space-y-0.5">
                              <div className="text-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {format(new Date(task.assignedAt), 'dd MMM yyyy')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(task.assignedAt), 'hh:mm a')}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="space-y-2">
                          <Badge
                            variant={
                              task.status === 'pending'
                                ? 'secondary'
                                : task.status === 'in-progress'
                                ? 'default'
                                : 'outline'
                            }
                            className={
                              task.status === 'completed'
                                ? 'bg-success/10 text-success border-success/30 capitalize'
                                : 'capitalize'
                            }
                          >
                            {task.status}
                          </Badge>
                          {activeTab === 'in-progress' && task.startedAt && task.assignedAt && (
                            <TimeTakenToStart assignedAt={task.assignedAt} startedAt={task.startedAt} />
                          )}
                        </div>
                      </TableCell>

                      {activeTab === 'todo' ? (
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleStartTask(task.id, task.patientUuid)}
                            disabled={isUpdating}
                            className="flex items-center gap-2"
                          >
                            <Play className="w-4 h-4" />
                            Start Task
                          </Button>
                        </TableCell>
                      ) : activeTab === 'completed' ? (
                        <TableCell colSpan={2}>
                          <div className="flex items-center gap-2">
                            {task.timeTakenMinutes !== undefined && task.allowedTatMinutes !== undefined && 
                             task.level1ThresholdMinutes !== undefined && task.level2ThresholdMinutes !== undefined ? (
                              <>
                                {(() => {
                                  let icon: JSX.Element;
                                  let label: string;
                                  let colorClass: string;
                                  
                                  if (task.timeTakenMinutes > task.allowedTatMinutes) {
                                    icon = <AlertCircle className="w-4 h-4 text-destructive" />;
                                    label = 'Exceeded';
                                    colorClass = 'text-destructive font-bold';
                                  } else if (task.timeTakenMinutes > task.level2ThresholdMinutes) {
                                    icon = <AlertCircle className="w-4 h-4 text-destructive" />;
                                    label = 'Level 2';
                                    colorClass = 'text-destructive';
                                  } else if (task.timeTakenMinutes > task.level1ThresholdMinutes) {
                                    icon = <AlertCircle className="w-4 h-4 text-warning" />;
                                    label = 'Level 1';
                                    colorClass = 'text-warning';
                                  } else {
                                    icon = <CheckCircle2 className="w-4 h-4 text-success" />;
                                    label = 'On Time';
                                    colorClass = 'text-success';
                                  }
                                  
                                  return (
                                    <>
                                      {icon}
                                      <span className={`text-sm font-medium ${colorClass}`}>
                                        {label} ({formatMinutesToTime(task.timeTakenMinutes)})
                                      </span>
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {task.timeTakenMinutes !== undefined 
                                  ? formatMinutesToTime(task.timeTakenMinutes)
                                  : task.elapsedTime || '00:00:00'
                                }
                              </span>
                            )}
                          </div>
                        </TableCell>
                      ) : activeTab === 'in-progress' ? (
                        <>
                          {/* Elapsed Time from assigned_at (for TAT) */}
                          <TableCell>
                            <div className={cn(
                              "text-sm font-medium font-mono",
                              getElapsedTimeColorClass(task.elapsedTime)
                            )}>
                              {task.elapsedTime}
                            </div>
                          </TableCell>
                          
                          {/* Total Patient Time */}
                          <TableCell>
                            <PatientTotalElapsedTime patientId={task.patientUuid} />
                          </TableCell>
                          
                          {/* Actions / Complete button removed for in-progress - disabled by design */}
                        </>
                      ) : null}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default MyTasks;
